import type { QRCodeProps } from '@ttsalpha/qrcode'

import { contrastRatio, ensureContrast } from '@/lib/color/contrast'
import type { QRCodeConfigType } from '@/lib/types/utilsT'

/**
 * The QR design model: every knob the customiser exposes, resolved to concrete values.
 *
 * What is saved per card is `QRCodeConfigType` (sparse, wire-shaped, carries the legacy
 * `useCompanyLogo` flag); what renders is `QrDesign` (complete, defaults applied). Everything
 * that draws a code — the share panel, the customiser preview, exports — goes through
 * `resolveQrDesign` → `buildQrProps`, so a saved design looks identical everywhere.
 */

export type QrDotStyle = 'square' | 'rounded' | 'circle'
export type QrCornerFrameStyle = 'square' | 'rounded' | 'extra-rounded' | 'circle'
export type QrCornerDotStyle = 'square' | 'rounded' | 'circle'
export type QrLogoSource = 'none' | 'company' | 'card' | 'icon' | 'upload'
export type QrLogoShape = 'square' | 'rounded' | 'circle'
/** How an icon logo is inked: brand colour on the backdrop, white on a brand tile, or the code's ink. */
export type QrLogoIconStyle = 'brand' | 'filled' | 'mono'

export type QrDesign = {
  foregroundColor: string
  backgroundColor: string
  dotStyle: QrDotStyle
  cornerFrameStyle: QrCornerFrameStyle
  cornerDotStyle: QrCornerDotStyle
  /** `null` follows `foregroundColor`. */
  cornerFrameColor: string | null
  cornerDotColor: string | null
  /** Quiet zone, in modules. */
  marginSize: number
  logoSource: QrLogoSource
  /** Icon key (see `lib/qr/logoIcons`) when `logoSource` is `icon`. */
  logoIcon: string | null
  logoIconStyle: QrLogoIconStyle
  /** Downscaled upload, as an image data URL, when `logoSource` is `upload`. */
  logoImage: string | null
  /** 0–1 of the largest logo the code can carry safely. */
  logoSize: number
  logoShape: QrLogoShape
  /** Tile behind the logo; `transparent` lets the code's background show through. */
  logoBackground: string
}

/** Stand-in value while a card has no link yet (onboarding, a new card) — the design still previews. */
export const QR_PLACEHOLDER_LINK = 'https://livecontact.com'

export const QR_DEFAULT_FG = '#1A1D1F'
export const QR_DEFAULT_BG = '#FFFFFF'
export const QR_LOGO_TRANSPARENT = 'transparent'

export const QR_LOGO_SIZE_MIN = 0.3
/** The library's own ceiling: at 1.0 a level-H code carries a logo ~30% of its width. */
export const QR_LOGO_SIZE_MAX = 1

export const DEFAULT_QR_DESIGN: QrDesign = {
  foregroundColor: QR_DEFAULT_FG,
  backgroundColor: QR_DEFAULT_BG,
  dotStyle: 'square',
  cornerFrameStyle: 'square',
  cornerDotStyle: 'square',
  cornerFrameColor: null,
  cornerDotColor: null,
  marginSize: 3,
  logoSource: 'none',
  logoIcon: null,
  logoIconStyle: 'brand',
  logoImage: null,
  logoSize: 0.8,
  logoShape: 'rounded',
  logoBackground: QR_DEFAULT_BG,
}

/** The centre style that reads as one family with each frame — applied when the frame changes. */
export const MATCHING_CORNER_DOT: Record<QrCornerFrameStyle, QrCornerDotStyle> = {
  square: 'square',
  rounded: 'square',
  'extra-rounded': 'rounded',
  circle: 'circle',
}

const pick = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  allowed.includes(value as T) ? (value as T) : fallback

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

/** Saved (sparse, possibly legacy) config → a complete design. Unknown values fall back. */
export const resolveQrDesign = (
  config?: QRCodeConfigType | null,
  base: Partial<QrDesign> = {}
): QrDesign => {
  const d = { ...DEFAULT_QR_DESIGN, ...base }
  const c = config || {}

  // Cards saved before logo sources existed only know `useCompanyLogo`.
  const legacySource: QrLogoSource = c.useCompanyLogo ? 'company' : d.logoSource

  return {
    foregroundColor: c.foregroundColor || d.foregroundColor,
    backgroundColor: c.backgroundColor || d.backgroundColor,
    dotStyle: pick(c.dotStyle, ['square', 'rounded', 'circle'], d.dotStyle),
    cornerFrameStyle: pick(
      c.cornerFrameStyle,
      ['square', 'rounded', 'extra-rounded', 'circle'],
      d.cornerFrameStyle
    ),
    cornerDotStyle: pick(c.cornerDotStyle, ['square', 'rounded', 'circle'], d.cornerDotStyle),
    cornerFrameColor: c.cornerFrameColor || null,
    cornerDotColor: c.cornerDotColor || null,
    marginSize: clamp(c.marginSize ?? d.marginSize, 0, 8),
    logoSource: pick(c.logoSource, ['none', 'company', 'card', 'icon', 'upload'], legacySource),
    logoIcon: c.logoIcon || null,
    logoIconStyle: pick(c.logoIconStyle, ['brand', 'filled', 'mono'], d.logoIconStyle),
    logoImage: c.logoImage || null,
    logoSize: clamp(c.logoSize ?? d.logoSize, QR_LOGO_SIZE_MIN, QR_LOGO_SIZE_MAX),
    logoShape: pick(c.logoShape, ['square', 'rounded', 'circle'], d.logoShape),
    logoBackground: c.logoBackground || d.logoBackground,
  }
}

/** Design → what the qr-design endpoint stores. Keeps `useCompanyLogo` for older readers. */
export const toQrConfig = (design: QrDesign, companyLogo?: string | null): QRCodeConfigType => ({
  ...design,
  logoIcon: design.logoSource === 'icon' ? design.logoIcon : null,
  logoImage: design.logoSource === 'upload' ? design.logoImage : null,
  useCompanyLogo: design.logoSource === 'company',
  image: design.logoSource === 'company' ? companyLogo || null : null,
})

/** Share of the code's edge a library `logo.size` of 1 clears (≈9% of the area, level H). */
const LOGO_MAX_AREA = 0.09
/** Quiet ring between the logo tile and the modules, as a fraction of the cleared square. */
const LOGO_RING = 0.06

/**
 * Error correction for a logo of this size — one level above the lowest the library would
 * pick. The library's thresholds are the break-even point; a printed code needs headroom.
 */
const logoEcl = (size: number): 'M' | 'Q' | 'H' => (size <= 0.25 ? 'M' : size <= 0.44 ? 'Q' : 'H')

type BuildArgs = {
  value: string
  size: number
  /** Composited logo (see `useQrLogo`); without one the code has no logo. */
  logoSrc?: string | null
  className?: string
  style?: React.CSSProperties
  ariaLabel?: string
}

/** Design → props for `<QRCode>`, `toDataURL` and `toSVGString`. Geometry scales with `size`. */
export const buildQrProps = (
  design: QrDesign,
  { value, size, logoSrc, className, style, ariaLabel }: BuildArgs
): QRCodeProps => {
  const hasLogo = Boolean(logoSrc)
  const logoSize = clamp(design.logoSize, QR_LOGO_SIZE_MIN, QR_LOGO_SIZE_MAX)
  const cleared = Math.sqrt(logoSize * LOGO_MAX_AREA) * size

  return {
    value,
    size,
    margin: design.marginSize,
    dotStyle: design.dotStyle,
    dotColor: design.foregroundColor,
    backgroundColor: design.backgroundColor,
    corner: {
      square: {
        style: design.cornerFrameStyle,
        color: design.cornerFrameColor || design.foregroundColor,
      },
      dot: {
        style: design.cornerDotStyle,
        color: design.cornerDotColor || design.foregroundColor,
      },
    },
    logo: hasLogo
      ? { src: logoSrc as string, size: logoSize, margin: cleared * LOGO_RING, hideDots: true }
      : undefined,
    qr: hasLogo ? { errorCorrectionLevel: logoEcl(logoSize) } : undefined,
    className,
    style,
    ariaLabel,
  }
}

/** 7:1 is comfortable for a phone camera in ordinary light; below 3:1 a code is unreliable. */
export const QR_CONTRAST_COMFORTABLE = 7
export const QR_CONTRAST_MINIMUM = 3
/**
 * Corner markers are large and solid, and scanners find them by their 1:1:3:1:1 rhythm rather
 * than module by module — an accent reads reliably from 4.5:1. That is what lets a coloured
 * corner work without costing scans.
 */
export const QR_CORNER_COMFORTABLE = 4.5

export type QrScanTone = 'good' | 'low' | 'bad'

/**
 * Scanners threshold on luminance, so every ink has to separate from the background. Returns
 * the verdict, both ratios, and whether the corners are the weak part.
 */
export const qrScanQuality = (design: QrDesign) => {
  const body = contrastRatio(design.foregroundColor, design.backgroundColor)
  const corners = Math.min(
    contrastRatio(design.cornerFrameColor || design.foregroundColor, design.backgroundColor),
    contrastRatio(design.cornerDotColor || design.foregroundColor, design.backgroundColor)
  )
  const tone: QrScanTone =
    body >= QR_CONTRAST_COMFORTABLE && corners >= QR_CORNER_COMFORTABLE
      ? 'good'
      : body >= QR_CONTRAST_MINIMUM && corners >= QR_CONTRAST_MINIMUM
        ? 'low'
        : 'bad'
  const weakCorners = corners / QR_CORNER_COMFORTABLE < body / QR_CONTRAST_COMFORTABLE
  return { tone, body, corners, weakCorners }
}

export type QrColorPreset = {
  name: string
  foregroundColor: string
  backgroundColor: string
  /** Accent on the corner frames; `null` keeps them in the module ink. */
  cornerColor: string | null
}

/**
 * A deep ink for the modules (≥7:1 on its paper) and, for most, a brighter accent on the corner
 * frames (≥4.5:1) — the colour people notice, at no cost to scanning.
 */
export const QR_COLOR_PRESETS: QrColorPreset[] = [
  { name: 'Classic', foregroundColor: '#111111', backgroundColor: '#FFFFFF', cornerColor: null },
  {
    name: 'Midnight',
    foregroundColor: '#0B1B3F',
    backgroundColor: '#FFFFFF',
    cornerColor: '#2551D6',
  },
  {
    name: 'Forest',
    foregroundColor: '#0B3B2B',
    backgroundColor: '#FFFFFF',
    cornerColor: '#15803D',
  },
  { name: 'Plum', foregroundColor: '#2E0A4F', backgroundColor: '#FFFFFF', cornerColor: '#7E22CE' },
  { name: 'Ember', foregroundColor: '#3A1206', backgroundColor: '#FFFFFF', cornerColor: '#C2410C' },
  { name: 'Rose', foregroundColor: '#4A0A1E', backgroundColor: '#FFFFFF', cornerColor: '#BE123C' },
  {
    name: 'Lagoon',
    foregroundColor: '#032B2A',
    backgroundColor: '#F0FDFA',
    cornerColor: '#0F766E',
  },
  { name: 'Sand', foregroundColor: '#2B2118', backgroundColor: '#FBF6EC', cornerColor: '#9A3412' },
  { name: 'Slate', foregroundColor: '#0F172A', backgroundColor: '#F1F5F9', cornerColor: '#475569' },
]

/** The card's own colour as a preset: deepened for the modules, kept brighter on the corners. */
export const brandQrPreset = (color: string): QrColorPreset => ({
  name: 'Brand',
  foregroundColor: ensureContrast(color, QR_DEFAULT_BG, QR_CONTRAST_COMFORTABLE),
  backgroundColor: QR_DEFAULT_BG,
  cornerColor: ensureContrast(color, QR_DEFAULT_BG, QR_CORNER_COMFORTABLE),
})
